import type { ReactNode } from "react";
import { Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";

/** Marks a control the API rejects when blank. Paired with <RequiredLegend /> once per form. */
export function RequiredMark() {
  return (
    <>
      <span aria-hidden="true" className="text-destructive">
        *
      </span>
      <span className="sr-only">(required)</span>
    </>
  );
}

/** Explains the asterisk. Rendered once, next to the submit buttons. */
export function RequiredLegend({ className }: { className?: string }) {
  return (
    <p className={cn("text-xs text-muted-foreground", className)}>
      <span aria-hidden="true" className="text-destructive">
        *
      </span>{" "}
      Required. Everything else is optional.
    </p>
  );
}

export interface FieldProps {
  /** Wired to the control's id via htmlFor — pass the same id to the input. */
  id?: string;
  label: ReactNode;
  required?: boolean;
  /** Persistent helper text. Hidden while an error is showing so the two never stack. */
  hint?: ReactNode;
  error?: string | null;
  className?: string;
  children: ReactNode;
}

export function Field({ id, label, required, hint, error, className, children }: FieldProps) {
  return (
    <div className={cn("min-w-0 space-y-1.5", className)}>
      <Label htmlFor={id} className="gap-1 text-xs text-muted-foreground">
        {label}
        {required && <RequiredMark />}
      </Label>
      {children}
      {error ? (
        <p className="text-[11px] leading-tight text-destructive">{error}</p>
      ) : hint ? (
        <p className="text-[11px] leading-tight text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

export function FormSection({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-0.5">
          <h3 className="text-sm font-semibold">{title}</h3>
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

export function LineCard({
  index,
  summary,
  onRemove,
  removeDisabled,
  invalid,
  children,
}: {
  index: number;
  /** Right-aligned summary (e.g. the line total) shown in the card header. */
  summary?: ReactNode;
  onRemove: () => void;
  removeDisabled?: boolean;
  invalid?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "rounded-lg border bg-muted/40 p-3 transition-colors",
        invalid && "border-destructive/50",
      )}
    >
      <div className="mb-2 flex items-center gap-2">
        <span className="text-xs font-semibold text-muted-foreground">Line {index + 1}</span>
        <div className="flex-1" />
        {summary}
        <RemoveLineButton onClick={onRemove} disabled={removeDisabled} index={index} />
      </div>
      {children}
    </div>
  );
}

function RemoveLineButton({
  onClick,
  disabled,
  index,
}: {
  onClick: () => void;
  disabled?: boolean;
  index: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={`Remove line ${index + 1}`}
      className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:pointer-events-none disabled:opacity-40"
    >
      <Trash2 className="size-3.5" />
    </button>
  );
}
